import React, { useMemo, useRef, useState } from "react";

import { useIntl } from "react-intl";

import { capitalize } from "Libs/utils";
import { selectFeatureResourceMetricsThresholdEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import TimeframeControl from "src/organization/pages/project/environment/metrics/components/timeframeControl/TimeframeControl";
import LargeMetric from "src/organization/pages/project/environment/metrics/containers/Service/LargeMetric";
import { ServiceUnitProps } from "src/organization/pages/project/environment/metrics/containers/Service/type";
import {
  serviceOrder,
  useAutoscallingValues
} from "src/organization/pages/project/environment/metrics/containers/Service/util";
import usePinnedTabsSync from "src/organization/pages/project/environment/metrics/hooks/usePinnedTabsSync";
import useStickyTimecontrol from "src/organization/pages/project/environment/metrics/hooks/useStickyTimecontrol";
import { MetricsContext } from "src/organization/pages/project/environment/metrics/resources";
import InstanceSelector from "src/organization/pages/project/environment/metrics/resources/Grid/InstanceSelector";
import usePoll from "src/organization/pages/project/environment/metrics/resources/Grid/usePoll";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { ParseTimeDurationType } from "src/organization/pages/project/environment/metrics/utils/parseDimensions/types";
import { usePollgroupContext } from "src/organization/pages/project/environment/metrics/utils/polling/usePollgroup";
import { useAppSelector } from "Store/hooks";

import MetricsHeader from "./MetricsHeader";
import { MainTitle, SticlyHeader } from "./ServiceMetrics.styles";
import * as S from "./ServiceMetrics.styles";

type Props = ServiceUnitProps & {
  enableDeployment: boolean;
  interval: ParseTimeDurationType;
  setEnableDeployment: (enable: boolean) => void;
  setTimeFrame: (timeframe: TimeFrameType) => void;
  lastUpdated?: Date;
};

const ServiceUnits = ({
  service,
  activities,
  selectedTimeframe,
  onBrush,
  kind,
  maxZoom,
  enableDeployment,
  interval,
  setEnableDeployment,
  setTimeFrame,
  lastUpdated
}: ServiceUnitProps<Props>) => {
  const [isLoading, onLoadingChange] = useState(true);
  const { getThreshold } = useAutoscallingValues(service.name);
  const { pollgroup } = usePollgroupContext();
  const [instanceHost, setInstanceHost] = useState<string[]>();
  const intl = useIntl();
  const enableThreshold = useAppSelector(
    selectFeatureResourceMetricsThresholdEnabled
  );
  const { metrics, instances } = usePoll({
    service: service,
    onLoadingChange,
    pollgroup,
    enabled: !!service
  });

  instances["prefixId"] = null;

  const { onSyncChange, isSynced, canJoinSync } = usePinnedTabsSync({
    setTimeframe: setTimeFrame,
    timeframe: selectedTimeframe
  });

  const deployments = useMemo(
    () => ({ enabled: enableDeployment, onChange: setEnableDeployment }),
    [enableDeployment, setEnableDeployment]
  );

  const handleSyncChange = (isSynced: boolean) => {
    onSyncChange(isSynced, selectedTimeframe);
  };

  const orderedMetrics = serviceOrder(metrics);
  const headerRef = useRef<HTMLDivElement>(null);
  useStickyTimecontrol({ ref: headerRef });
  const selectedHost = instanceHost || instances || [];

  return (
    <MetricsContext.Provider value={{ enableThreshold }}>
      <SticlyHeader ref={headerRef}>
        <MainTitle>{intl.formatMessage({ id: "metrics.title" })}</MainTitle>
        <TimeframeControl
          timeframe={{
            onChange: setTimeFrame,
            timeframe: selectedTimeframe
          }}
          pollInterval={interval}
          lastUpdated={lastUpdated}
          deployment={deployments}
          tabSync={{
            onSyncChange: handleSyncChange,
            isSynced,
            canJoinSync
          }}
        >
          {instances.length > 1 && (
            <InstanceSelector
              instances={instances}
              selected={selectedHost}
              onChange={setInstanceHost}
            />
          )}
        </TimeframeControl>
      </SticlyHeader>
      <S.ChartLayout>
        <MetricsHeader service={service} />
        <S.GridLG>
          {orderedMetrics.map(([serviceUnit, value]) => {
            const autoscaleConfig = getThreshold({ service, serviceUnit });
            return (
              <LargeMetric
                key={serviceUnit}
                isLoading={isLoading}
                onBrush={onBrush}
                hosts={selectedHost}
                selectedTimeframe={selectedTimeframe}
                name={serviceUnit}
                value={value}
                kind={kind}
                serviceKind={capitalize(service?.kind || service["class"])}
                activities={activities}
                deployment={deployments.enabled}
                maxZoom={maxZoom}
                autoscaleConfig={autoscaleConfig}
              />
            );
          })}
        </S.GridLG>
      </S.ChartLayout>
    </MetricsContext.Provider>
  );
};
export default ServiceUnits;
